/**
 * useProgressSteps — Derive progress steps from Glass Box observability events (Spec 167 Phase 3)
 *
 * Maps curated observability events to human-readable progress steps
 * displayed in the ProgressCard component during agent loop execution.
 */

import { useMemo } from 'react';
import type { GlassBoxItem } from './useGlassBoxEvents';

export interface ProgressStep {
  id: string;
  label: string;
  status: 'active' | 'completed';
  timestamp: number;
  durationMs?: number;
}

/**
 * Keyed on the names the DO broadcasts (`CURATED_OBSERVABILITY_EVENTS`). Live frames
 * carry them as-is and the backfill route maps persisted Axiom names onto them, so
 * no persisted-only name (`agent_loop_llm_call_*`, `sandbox_tool_*`) belongs here (#12591).
 */
const EVENT_LABELS: Record<string, string> = {
  sprite_provision_requested: 'Provisioning sandbox...',
  sprite_provision_completed: 'Sandbox ready',
  warm_pool_hit: 'Claimed pre-warmed sandbox',
  warm_pool_miss: 'No warm sandbox available, provisioning...',
  opencode_session_create_duration_ms: 'OpenCode session created',
  opencode_session_cold_start_retry: 'Waiting for OpenCode to start...',
  llm_call_start: 'Thinking...',
  llm_call_complete: 'Thought complete',
  tool_dispatch: 'Running tool...',
  tool_complete: 'Tool completed',
  agent_loop_yield: 'Continuing...',
  rate_limit_warning: 'Rate limit approaching, throttling...',
  agent_loop_started: 'Agent started',
  agent_loop_complete: 'Agent complete',
  agent_loop_error: 'Error occurred',
};

/** Events that represent "in progress" (active status) */
/** Events that represent "in progress" (active status) */
const ACTIVE_EVENTS = new Set([
  'sprite_provision_requested',
  'warm_pool_miss',
  'opencode_session_cold_start_retry',
  'llm_call_start',
  'tool_dispatch',
  'agent_loop_yield',
  'rate_limit_warning',
  'agent_loop_started',
  'agent_loop_error',  // Error events stay "active" (show as error indicator, not green check)
]);

/** Event names this hook turns into steps — pinned to the shared broadcast list by a test. */
export const PROGRESS_STEP_EVENTS: readonly string[] = Object.keys(EVENT_LABELS);

/** Format a label with event-specific data */
function formatLabel(event: string, data: Record<string, unknown>): string {
  const base = EVENT_LABELS[event];
  if (!base) return event;

  switch (event) {
    case 'llm_call_complete': {
      const durationS = typeof data.duration_ms === 'number'
        ? (data.duration_ms / 1000).toFixed(1)
        : null;
      const tokens = typeof data.output_tokens === 'number'
        ? data.output_tokens.toLocaleString()
        : null;
      if (durationS && tokens) return `Thought for ${durationS}s (${tokens} tokens)`;
      if (durationS) return `Thought for ${durationS}s`;
      return base;
    }
    case 'tool_dispatch': {
      // The DO broadcasts one frame per batch: tool_names is an array.
      const names = Array.isArray(data.tool_names)
        ? data.tool_names.filter((n): n is string => typeof n === 'string')
        : [];
      return names.length > 0 ? `Running: ${names.join(', ')}...` : base;
    }
    case 'tool_complete': {
      const names = Array.isArray(data.tool_names)
        ? data.tool_names.filter((n): n is string => typeof n === 'string')
        : [];
      const durationS = typeof data.total_duration_ms === 'number'
        ? (data.total_duration_ms / 1000).toFixed(1)
        : null;
      const head = names.length > 0 ? `Completed: ${names.join(', ')}` : base;
      return durationS ? `${head} (${durationS}s)` : head;
    }
    case 'opencode_session_cold_start_retry': {
      const attempt = typeof data.attempt === 'number' ? data.attempt : null;
      return attempt ? `Waiting for OpenCode to start (retry ${attempt})...` : base;
    }
    case 'agent_loop_yield': {
      const cycle = typeof data.alarm_cycle === 'number' ? data.alarm_cycle : null;
      return cycle !== null ? `Continuing (cycle ${cycle + 1})...` : base;
    }
    default:
      return base;
  }
}

/**
 * Convert Glass Box events into progress steps for the ProgressCard.
 *
 * Only includes events that have a mapping in EVENT_LABELS.
 * Active/completed status is determined by the event type.
 */
export function useProgressSteps(events: GlassBoxItem[]): ProgressStep[] {
  return useMemo(() => {
    const steps: ProgressStep[] = [];

    for (const item of events) {
      const label = EVENT_LABELS[item.event];
      if (!label) continue; // Skip events without a progress mapping

      steps.push({
        id: item.id,
        label: formatLabel(item.event, item.data),
        status: ACTIVE_EVENTS.has(item.event) ? 'active' : 'completed',
        timestamp: item.receivedAt,
        durationMs: typeof item.data.duration_ms === 'number'
          ? item.data.duration_ms
          : typeof item.data.total_duration_ms === 'number'
            ? item.data.total_duration_ms
            : undefined,
      });
    }

    // Mark all steps as completed except the last active one
    for (let i = 0; i < steps.length - 1; i++) {
      if (steps[i].status === 'active') {
        steps[i].status = 'completed';
      }
    }

    return steps;
  }, [events]);
}
